class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => this.addUpsell(event));
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' && this.classList.contains('active')) this.loadUpsells();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.loadUpsells();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  async loadUpsells() {
    const area = this.querySelector('.caprus-cart-upsell');
    if (!area) return;
    const productId = area.dataset.productId;
    const inCart = new Set((area.dataset.cartProductIds || '').split(','));
    if (!productId || !this.classList.contains('active')) {
      area.hidden = true;
      return;
    }
    const requestId = (this.upsellRequestId || 0) + 1;
    this.upsellRequestId = requestId;
    const items = area.querySelector('.caprus-cart-upsell__items');
    area.hidden = !items.querySelector('.caprus-cart-upsell-card');
    const getCards = async (intent) => {
      const url = new URL(window.Shopify.routes.root + 'recommendations/products', window.location.origin);
      url.searchParams.set('section_id', 'caprus-cart-recommendations');
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', intent);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Recommendations unavailable');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = [...documentFragment.querySelectorAll('.caprus-cart-upsell-card')]
        .filter((card) => !inCart.has(card.dataset.productId)).slice(0, 2);
      if (!cards.length) throw new Error('No recommendations');
      return cards;
    };
    try {
      const cards = await Promise.any(['complementary', 'related'].map(getCards));
      if (requestId !== this.upsellRequestId || !this.contains(area)) return;
      area.querySelector('.caprus-cart-upsell__items').replaceChildren(...cards);
      area.hidden = false;
    } catch (error) {
      if (requestId !== this.upsellRequestId) return;
    }
  }

  async addUpsell(event) {
    const button = event.target.closest('[data-caprus-upsell-add]');
    if (!button || !this.contains(button) || button.disabled) return;
    const card = button.closest('.caprus-cart-upsell-card');
    const variantId = card.querySelector('.caprus-cart-upsell-card__variant').value;
    const error = this.querySelector('.caprus-cart-upsell__error');
    button.disabled = true;
    error.hidden = true;
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status || !result.sections?.['cart-drawer']) {
        throw new Error(result.description || window.cartStrings.error);
      }
      this.renderContents(result);
    } catch (failure) {
      error.textContent = failure.message || window.cartStrings.error;
      error.hidden = false;
      button.disabled = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
